// Feature P6: debris rules. Pure data and lookups; leaves.ts, the tools and
// the catcher apply them when the sim runs with `debrisRules`.
//
//   Scoring: a loose leaf counts against a clear on paved cells, bed cells and
//   deck/step prop footprints (props.ts `debrisSurface`).
//   Beds: only the rake takes a leaf out of a bed. A leaf that isn't being
//   raked can't leave a bed cell, and a leaf entering one slows hard
//   (BED_FRICTION), so the blower only pushes it further in.
//   Clear vs collect: `blow` is the clear (off the property or into a pile is
//   fine), `collect` is on the tarp and bagged. A catcher counts 100% toward
//   the clear and 60% per pass (CATCHER_PER_PASS) toward the collect.
//   Off the property: counts as cleared, less OFF_PROPERTY_NUDGE_PCT on the
//   clear task, and the breakdown says COUNCIL_COPY.

import type { Layout, TaskKind } from './types';
import { cellAt, isSolidType, pointInProp, propHalfExtents, T_BED, T_PAVED, type SimGrid } from './layout';
import { isDebrisSurface } from './props';

/** Thoroughness points a clear task loses when leaves went over the fence. */
export const OFF_PROPERTY_NUDGE_PCT = 2;
export const COUNCIL_COPY = "Council won't love that.";
/** Velocity decay per second for an un-raked leaf in a bed (vs LEAF_FRICTION 3 on open ground). */
export { BED_FRICTION } from './leaves';

/** Player-facing names for the two debris variants. The stored task labels are unchanged. */
export const DEBRIS_TASK_LABEL: Partial<Record<TaskKind, string>> = {
  blow: 'Clear leaves',
  collect: 'Collect & bag',
};

/** The label to show for a task: "Clear leaves" / "Collect & bag" for the debris tasks with the rules on. */
export function debrisTaskLabel(t: { kind: TaskKind; label: string }, on = true): string {
  return (on && DEBRIS_TASK_LABEL[t.kind]) || t.label;
}

/**
 * Per cell: 1 where a loose leaf counts against a clear (paved, bed, or the
 * centre inside a non-solid deck/step prop footprint), else 0.
 */
export function debrisCells(layout: Layout, grid: SimGrid): Uint8Array {
  const out = new Uint8Array(grid.type.length);
  for (let i = 0; i < out.length; i++) if (grid.type[i] === T_PAVED || grid.type[i] === T_BED) out[i] = 1;
  const cm = grid.cellM;
  for (const p of layout.props) {
    if (p.solid || !isDebrisSurface(p)) continue;
    const [ex, ey] = propHalfExtents(p);
    const x0 = Math.max(0, Math.floor((p.x - ex) / cm));
    const x1 = Math.min(grid.w - 1, Math.floor((p.x + ex) / cm));
    const y0 = Math.max(0, Math.floor((p.y - ey) / cm));
    const y1 = Math.min(grid.h - 1, Math.floor((p.y + ey) / cm));
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const i = cy * grid.w + cx;
        if (!isSolidType(grid.type[i]) && pointInProp(p, (cx + 0.5) * cm, (cy + 0.5) * cm)) out[i] = 1;
      }
    }
  }
  return out;
}

/** Is world (x, y) on a bed cell? */
export function onBed(grid: SimGrid, x: number, y: number): boolean {
  const i = cellAt(grid, x, y);
  return i >= 0 && grid.type[i] === T_BED;
}
